import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import type {
  DatosCuadre,
  ResultadoCuadre,
} from '#components/mobile/inicio/cuadre';
import { Boton } from '#components/mobile/ui/Boton';
import { Hoja } from '#components/mobile/ui/Hoja';
import { color, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';

type Props = {
  datos: DatosCuadre;
  resultado: ResultadoCuadre;
  onCerrar: () => void;
};

/**
 * Desglose del cuadre del presupuesto (ver `cuadre.ts`) en lenguaje
 * sencillo: lo que hay hoy en las cuentas frente a lo repartido. Las líneas
 * que valen 0 y no son parte de la idea básica no se muestran.
 */
export function HojaCuadre({ datos, resultado, onCerrar }: Props) {
  const { t } = useTranslation();
  const format = useFormat();

  const lineas: { clave: string; texto: string; importe: number }[] = [
    {
      clave: 'listo',
      texto: t('Ready to assign'),
      importe: datos.listoParaAsignar,
    },
    {
      clave: 'disponible',
      texto: t('Available in your categories this month'),
      importe: datos.disponible,
    },
    {
      clave: 'futuro',
      texto: t('Assigned in the coming months'),
      importe: datos.asignadoFuturo,
    },
    {
      clave: 'guardado',
      texto: t('Saved for next month'),
      importe: datos.guardado,
    },
    {
      // Ya restan del disponible, aún no del saldo: se suman de vuelta.
      clave: 'apuntados-futuros',
      texto: t('Transactions entered for days that have not arrived yet'),
      importe: -datos.apuntadosFuturos,
    },
    {
      // Solo al mirar un mes pasado.
      clave: 'meses-siguientes',
      texto: t('Transactions dated in the coming months'),
      importe: datos.mesesSiguientes,
    },
    {
      clave: 'sin-categoria',
      texto: t('Uncategorized transactions'),
      importe: datos.sinCategoria,
    },
  ].filter(
    l =>
      l.importe !== 0 ||
      l.clave === 'listo' ||
      l.clave === 'disponible' ||
      l.clave === 'futuro',
  );

  const fila = (etiqueta: string, importe: number, fuerte = false) => (
    <View
      key={etiqueta}
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        padding: '8px 0',
        borderTop: fuerte ? `1px solid ${color.line}` : undefined,
      }}
    >
      <Text style={{ fontWeight: fuerte ? 700 : 400 }}>{etiqueta}</Text>
      <PrivacyFilter>
        <Text
          style={{
            fontWeight: fuerte ? 700 : 500,
            fontVariantNumeric: 'tabular-nums',
            color: fuerte ? color.bad : undefined,
          }}
        >
          {format(importe, 'financial')}
        </Text>
      </PrivacyFilter>
    </View>
  );

  return (
    <Hoja
      aria-label={t("The budget doesn't add up")}
      data-testid="hoja-cuadre"
      onCerrar={onCerrar}
    >
      <Text style={{ ...texto.titulo, marginBottom: 8 }}>
        <Trans>The budget doesn't add up</Trans>
      </Text>
      <Text style={{ color: color.fg2, marginBottom: 12 }}>
        <Trans>
          The money in your budget accounts should match, to the cent, the money
          shared out in your budget. Uncategorized transactions are already
          taken into account, so this difference means something is wrong with
          the data.
        </Trans>
      </Text>

      {fila(t('Money in your budget accounts'), datos.saldoCuentas)}
      {lineas.map(l => fila(l.texto, l.importe))}
      {fila(
        resultado.descuadre < 0 ? t('Missing') : t('Too much'),
        Math.abs(resultado.descuadre),
        true,
      )}

      <Text style={{ color: color.fg2, margin: '12px 0' }}>
        <Trans>
          Don't move money to hide it: check your latest changes (deleted
          categories or transactions) or restore a backup.
        </Trans>
      </Text>
      <Boton variante="primario" onPress={onCerrar}>
        <Trans>Close</Trans>
      </Boton>
    </Hoja>
  );
}
